import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

import { getCategory } from "@/lib/categories";
import { getAllEvents } from "@/lib/storage";
import {
  todayISO,
  toISODate,
  parseISODate,
  formatHM,
  formatShortDate,
  pad2,
} from "@/lib/time";
import { useNow } from "@/hooks/useNow";
import { useLocalPref } from "@/hooks/useLocalPref";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/i18n";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const DAY_MS = 24 * 60 * 60 * 1000;

// Absolute start/end timestamps for an event, honouring cross-midnight events
// (end lands on the next day). Reuses the same event fields as the rest of the app.
function eventBounds(e) {
  if (!e || !e.date) return null;
  const sh = Number(e.startHour);
  const sm = Number(e.startMinute);
  const eh = Number(e.endHour);
  const em = Number(e.endMinute);
  if (![sh, sm, eh, em].every(Number.isFinite)) return null;
  const start = parseISODate(e.date);
  if (!start || Number.isNaN(start.getTime())) return null;
  start.setHours(sh, sm, 0, 0);
  const end = parseISODate(e.date);
  end.setHours(eh, em, 0, 0);
  let endMs = end.getTime();
  const startMs = start.getTime();
  if (e.crossMidnight || endMs <= startMs) endMs += DAY_MS;
  return { startMs, endMs };
}

const RANGE_IDS = [
  { id: "6h", minutes: 6 * 60 },
  { id: "12h", minutes: 12 * 60 },
  { id: "24h", minutes: 24 * 60 },
  { id: "3d", minutes: 3 * 24 * 60 },
  { id: "7d", minutes: 7 * 24 * 60 },
];

export default function NextEventCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const now = useNow(30 * 1000);
  const [showPct] = useLocalPref("chronicles.pref.showProgressPct", false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewDate, setPreviewDate] = useState(todayISO());
  const [rangeId, setRangeId] = useState("24h");
  const [events, setEvents] = useState([]);
  // Manual time = user has chosen a specific clock time; otherwise the card uses the live clock.
  const [manualTime, setManualTime] = useState(false);
  const [previewHour, setPreviewHour] = useState(() => new Date().getHours());
  const [previewMinute, setPreviewMinute] = useState(() => new Date().getMinutes());

  useEffect(() => {
    const load = () => setEvents(getAllEvents());
    load();
    window.addEventListener("chronicles:changed", load);
    return () => window.removeEventListener("chronicles:changed", load);
  }, []);

  const range = RANGE_IDS.find((r) => r.id === rangeId);

  const isLiveNow = !manualTime && previewDate === todayISO();

  const resetToNow = () => {
    setManualTime(false);
    setPreviewDate(todayISO());
    setPreviewHour(new Date().getHours());
    setPreviewMinute(new Date().getMinutes());
  };

  // Find the next event whose start is >= reference moment (manual or live)
  const nextEvent = useMemo(() => {
    if (!events.length) return null;
    let referenceMs;
    if (isLiveNow) {
      referenceMs = now.getTime();
    } else {
      const pivot = parseISODate(previewDate);
      pivot.setHours(previewHour, previewMinute, 0, 0);
      referenceMs = pivot.getTime();
    }
    const windowEndMs = referenceMs + range.minutes * 60 * 1000;

    let best = null;
    for (const e of events) {
      if (!e || !e.date) continue;
      const sh = Number(e.startHour);
      const sm = Number(e.startMinute);
      const eh = Number(e.endHour);
      const em = Number(e.endMinute);
      // Skip malformed events (missing/invalid start or end) so the card never
      // renders "undefined:undefined" or "NaNm".
      if (![sh, sm, eh, em].every(Number.isFinite)) continue;
      const d = parseISODate(e.date);
      if (!d || Number.isNaN(d.getTime())) continue;
      d.setHours(sh, sm, 0, 0);
      const startMs = d.getTime();
      if (!Number.isFinite(startMs)) continue;
      if (startMs < referenceMs) continue;
      if (startMs > windowEndMs) continue;
      if (!best || startMs < best.startMs) {
        best = { ev: e, startMs };
      }
    }
    return best;
  }, [events, previewDate, previewHour, previewMinute, range, now, isLiveNow]);

  const remaining = useMemo(() => {
    if (!nextEvent) return null;
    const diff = nextEvent.startMs - now.getTime();
    if (diff <= 0) return t("nextEvent.now");
    const minutes = Math.floor(diff / 60000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h >= 24) {
      const d = Math.floor(h / 24);
      const remH = h % 24;
      return `${d}d ${remH}h`;
    }
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }, [nextEvent, now, t]);

  const isToday = previewDate === todayISO();

  // Currently-active event (live clock only). Picks the most recently started
  // event whose window contains "now"; cross-midnight handled by eventBounds.
  const activeEvent = useMemo(() => {
    if (!isLiveNow || !events.length) return null;
    const nowMs = now.getTime();
    let best = null;
    for (const e of events) {
      const b = eventBounds(e);
      if (!b) continue;
      if (b.startMs <= nowMs && nowMs < b.endMs) {
        if (!best || b.startMs > best.startMs) best = { ev: e, ...b };
      }
    }
    return best;
  }, [events, now, isLiveNow]);

  const activeInfo = useMemo(() => {
    if (!activeEvent) return null;
    const nowMs = now.getTime();
    const total = activeEvent.endMs - activeEvent.startMs;
    const elapsed = Math.min(Math.max(nowMs - activeEvent.startMs, 0), total);
    const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
    const remMs = Math.max(activeEvent.endMs - nowMs, 0);
    const mins = Math.floor(remMs / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const time = h > 0 ? `${h} h ${m} min` : `${m} min`;
    return { pct, time };
  }, [activeEvent, now]);

  // After clicking the date picker — also flip into manual mode so the time selector is honored.
  const onPickDate = (iso) => {
    setPreviewDate(iso);
    setManualTime(true);
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-2.5 sm:p-3"
      data-testid="next-event-card"
    >
      <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t("nextEvent.label")}</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="text-xs text-slate-700 px-2.5 h-7 rounded-full border border-slate-300 hover:bg-slate-50 transition font-mono-tab"
                data-testid="next-event-date-trigger"
              >
                {isLiveNow ? t("nextEvent.today") : (isToday ? t("nextEvent.today") : formatShortDate(previewDate))}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-auto rounded-2xl">
              <CalendarPicker
                mode="single"
                selected={parseISODate(previewDate)}
                onSelect={(d) => {
                  if (d) {
                    onPickDate(toISODate(d));
                    setPickerOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Manual hour / minute selectors */}
          <Select
            value={String(previewHour)}
            onValueChange={(v) => {
              setPreviewHour(Number(v));
              setManualTime(true);
            }}
          >
            <SelectTrigger
              className="h-7 px-2 rounded-full border-slate-300 text-xs bg-white w-auto gap-1 font-mono-tab"
              data-testid="next-event-hour-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)} data-testid={`next-event-hour-${h}`}>
                  {pad2(h)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-slate-400 text-xs font-mono-tab">:</span>
          <Select
            value={String(previewMinute)}
            onValueChange={(v) => {
              setPreviewMinute(Number(v));
              setManualTime(true);
            }}
          >
            <SelectTrigger
              className="h-7 px-2 rounded-full border-slate-300 text-xs bg-white w-auto gap-1 font-mono-tab"
              data-testid="next-event-minute-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {MINUTES.map((m) => (
                <SelectItem key={m} value={String(m)} data-testid={`next-event-minute-${m}`}>
                  {pad2(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!isLiveNow ? (
            <button
              onClick={resetToNow}
              className="text-xs text-slate-700 px-2.5 h-7 rounded-full border border-slate-300 bg-slate-50 hover:bg-slate-100 transition"
              data-testid="next-event-reset"
            >
              {t("nextEvent.resetToNow")}
            </button>
          ) : null}

          <Select value={rangeId} onValueChange={setRangeId}>
            <SelectTrigger
              className="h-7 px-2.5 rounded-full border-slate-300 text-xs bg-white w-auto gap-1"
              data-testid="next-event-range-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {RANGE_IDS.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`next-event-range-${r.id}`}>
                  {t(`nextEvent.ranges.${r.id}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {activeEvent ? (
        <div data-testid="current-next-stack" className="space-y-1">
          {/* Current event */}
          <button
            onClick={() => navigate(`/calendar/${activeEvent.ev.date}`)}
            className="w-full text-left block rounded-lg hover:bg-slate-50 -mx-1.5 px-1.5 py-0.5 transition"
            data-testid="active-event-body"
          >
            <div className="flex items-center gap-2.5">
              <span
                className="h-7 w-7 rounded-md border border-black/5 shrink-0"
                style={{ backgroundColor: getCategory(activeEvent.ev.category).hex }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-slate-900 truncate leading-tight" data-testid="active-event-title">
                  {activeEvent.ev.title}
                </p>
                <p className="text-[11px] text-slate-500 font-mono-tab leading-tight" data-testid="active-event-when">
                  {formatHM(activeEvent.ev.startHour, activeEvent.ev.startMinute)}–
                  {formatHM(activeEvent.ev.endHour, activeEvent.ev.endMinute)}
                </p>
              </div>
              <p className="text-[11px] text-slate-600 font-mono-tab shrink-0 text-right" data-testid="active-event-remaining">
                {showPct
                  ? t("nextEvent.remainingWithPct", { time: activeInfo.time, pct: activeInfo.pct })
                  : t("nextEvent.remaining", { time: activeInfo.time })}
              </p>
            </div>
            <Progress value={activeInfo.pct} className="h-1 mt-1" data-testid="active-event-progress" />
          </button>

          {/* Next event — equal visual weight, no second progress bar */}
          {nextEvent ? (
            <button
              onClick={() => navigate(`/calendar/${nextEvent.ev.date}`)}
              className="w-full text-left flex items-center gap-2.5 rounded-lg hover:bg-slate-50 -mx-1.5 px-1.5 py-0.5 border-t border-border pt-1 transition"
              data-testid="next-event-preview"
            >
              <span
                className="h-7 w-7 rounded-md border border-black/5 shrink-0"
                style={{ backgroundColor: getCategory(nextEvent.ev.category).hex }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-slate-900 truncate leading-tight" data-testid="next-event-preview-title">
                  {nextEvent.ev.title}
                </p>
                <p className="text-[11px] text-slate-500 font-mono-tab leading-tight" data-testid="next-event-preview-when">
                  {nextEvent.ev.date === todayISO() ? "" : `${formatShortDate(nextEvent.ev.date)} · `}
                  {formatHM(nextEvent.ev.startHour, nextEvent.ev.startMinute)}–
                  {formatHM(nextEvent.ev.endHour, nextEvent.ev.endMinute)}
                </p>
              </div>
              <p className="text-[11px] text-slate-600 font-mono-tab shrink-0 text-right" data-testid="next-event-preview-countdown">
                {remaining}
                {" · "}
                <span className="uppercase tracking-[0.12em] text-slate-400 font-semibold">{t("nextEvent.startsIn")}</span>
              </p>
            </button>
          ) : null}
        </div>
      ) : nextEvent ? (
        <button
          onClick={() => navigate(`/calendar/${nextEvent.ev.date}`)}
          className="w-full text-left flex items-center gap-2.5 rounded-lg hover:bg-slate-50 -mx-1.5 px-1.5 py-1 transition"
          data-testid="next-event-body"
        >
          <span
            className="h-7 w-7 rounded-md border border-black/5 shrink-0"
            style={{ backgroundColor: getCategory(nextEvent.ev.category).hex }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-slate-900 truncate leading-tight" data-testid="next-event-title">
              {nextEvent.ev.title}
            </p>
            <p className="text-[11px] text-slate-500 font-mono-tab leading-tight" data-testid="next-event-when">
              {nextEvent.ev.date === todayISO() ? "" : `${formatShortDate(nextEvent.ev.date)} · `}
              {formatHM(nextEvent.ev.startHour, nextEvent.ev.startMinute)}–
              {formatHM(nextEvent.ev.endHour, nextEvent.ev.endMinute)}
            </p>
          </div>
          <p className="text-[11px] text-slate-600 font-mono-tab shrink-0 text-right" data-testid="next-event-countdown">
            {remaining}
            {" · "}
            <span className="uppercase tracking-[0.12em] text-slate-400 font-semibold">{t("nextEvent.startsIn")}</span>
          </p>
        </button>
      ) : (
        <div className="py-1.5 text-sm text-slate-500" data-testid="next-event-empty">
          {t("nextEvent.empty")}
        </div>
      )}
    </section>
  );
}
